import { createFileRoute } from '@tanstack/react-router';
import { Plus, Users } from 'lucide-react';
import { useState } from 'react';

import { UserFormModal } from '@apps/web/components/admin/user_form_modal';
import { AdminBadge } from '@apps/web/features/admin_design/admin_badge';
import {
    AdminButton,
    AdminSearchInput,
    AdminSelect,
} from '@apps/web/features/admin_design/admin_controls';
import {
    AdminPage,
    AdminPageBody,
    AdminPageHeader,
} from '@apps/web/features/admin_design/admin_shell';
import { AdminTableSkeleton } from '@apps/web/features/admin_design/admin_skeleton';
import {
    AdminEmptyState,
    AdminErrorState,
} from '@apps/web/features/admin_design/admin_states';
import {
    AdminTable,
    AdminTableBody,
    AdminTableCell,
    AdminTableHeadCell,
    AdminTableHeader,
    AdminTableRow,
} from '@apps/web/features/admin_design/admin_table';
import { orgMemberRoleLabel } from '@apps/web/lib/organization_permissions';
import { useAdminUsers } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type OrgMemberRole } from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/users/')({
    head: () => ({
        meta: [
            {
                title: 'Users | Phoebe Admin',
            },
        ],
    }),
    component: AdminUsersPage,
});

const USER_TABLE_COLUMNS = [
    'Name',
    'Email',
    'Organizations',
    'Role',
    'Status',
    'Created',
] as const;

const ROLE_FILTER_OPTIONS = [
    { value: 'all', label: 'All Roles' },
    { value: 'admin', label: 'Admin' },
    { value: 'scheduler', label: 'Scheduler' },
    { value: 'restricted_scheduler', label: 'Restricted Scheduler' },
] as const;

function AdminUsersPage() {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRole, setSelectedRole] = useState<OrgMemberRole | 'all'>(
        'all',
    );
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const {
        data: usersResponse,
        isLoading,
        error,
        mutate: mutateUsers,
    } = useAdminUsers({
        page: 1,
        per_page: 100,
        search: searchQuery,
        ...(selectedRole !== 'all' && { role: selectedRole }),
    });

    const users = usersResponse?.users ?? [];

    return (
        <AdminPage>
            <AdminPageHeader
                title="Users"
                meta={`${usersResponse?.total ?? 0} total`}
                actions={
                    <AdminButton
                        onClick={() => setIsCreateModalOpen(true)}
                        variant="primary"
                    >
                        <Plus strokeWidth={1.75} />
                        Create User
                    </AdminButton>
                }
            >
                <div className="flex flex-wrap items-center gap-2">
                    <AdminSearchInput
                        placeholder="Search by name, email, or phone…"
                        containerClassName="w-full max-w-md"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <AdminSelect
                        aria-label="Filter by role"
                        value={selectedRole}
                        onValueChange={(value) =>
                            setSelectedRole(value as OrgMemberRole | 'all')
                        }
                        options={ROLE_FILTER_OPTIONS}
                    />
                </div>
            </AdminPageHeader>
            <AdminPageBody>
                {isLoading ? (
                    <AdminTableSkeleton columns={USER_TABLE_COLUMNS} />
                ) : error !== undefined ? (
                    <AdminErrorState
                        title="Failed to load users"
                        description="Please try again."
                        onRetry={() => {
                            void mutateUsers();
                        }}
                    />
                ) : users?.length === 0 ? (
                    <AdminEmptyState
                        icon={Users}
                        title={searchQuery ? 'No users found' : 'No users yet'}
                        description={
                            searchQuery
                                ? 'Try a different name, email, or phone number.'
                                : undefined
                        }
                    />
                ) : (
                    <AdminTable>
                        <AdminTableHeader>
                            <tr>
                                {USER_TABLE_COLUMNS.map((column) => (
                                    <AdminTableHeadCell key={column}>
                                        {column}
                                    </AdminTableHeadCell>
                                ))}
                            </tr>
                        </AdminTableHeader>
                        <AdminTableBody>
                            {users.map((user) => (
                                <AdminTableRow
                                    key={user.id}
                                    className="cursor-pointer"
                                    onClick={() => {
                                        window.open(
                                            `/admin/users/${user.id}`,
                                            '_blank',
                                            'noopener,noreferrer',
                                        );
                                    }}
                                >
                                    <AdminTableCell className="text-admin-ink font-medium">
                                        {user.first_name || user.last_name ? (
                                            `${user.first_name || ''} ${user.last_name || ''}`.trim()
                                        ) : (
                                            <span className="text-admin-ink-faint font-normal">
                                                No name
                                            </span>
                                        )}
                                    </AdminTableCell>
                                    <AdminTableCell>
                                        {user.email || (
                                            <span className="text-admin-ink-faint">
                                                —
                                            </span>
                                        )}
                                    </AdminTableCell>
                                    <AdminTableCell>
                                        {(user.organizations ?? []).length >
                                        0 ? (
                                            <span className="tabular-nums">
                                                {
                                                    (user.organizations ?? [])
                                                        .length
                                                }{' '}
                                                <span className="text-admin-ink-muted">
                                                    org
                                                    {(user.organizations ?? [])
                                                        .length === 1
                                                        ? ''
                                                        : 's'}
                                                </span>
                                            </span>
                                        ) : (
                                            <span className="text-admin-ink-faint">
                                                None
                                            </span>
                                        )}
                                    </AdminTableCell>
                                    <AdminTableCell>
                                        {(user.organizations ?? [])[0]?.role ? (
                                            <AdminBadge tone="outline">
                                                {orgMemberRoleLabel(
                                                    (user.organizations ??
                                                        [])[0]?.role,
                                                )}
                                            </AdminBadge>
                                        ) : (
                                            <span className="text-admin-ink-faint">
                                                —
                                            </span>
                                        )}
                                    </AdminTableCell>
                                    <AdminTableCell>
                                        <div className="flex items-center gap-1">
                                            {user.admin && (
                                                <AdminBadge
                                                    variant="solid"
                                                    tone="ink-primary"
                                                >
                                                    Admin
                                                </AdminBadge>
                                            )}
                                            {user.is_gated && (
                                                <AdminBadge tone="outline">
                                                    Gated
                                                </AdminBadge>
                                            )}
                                        </div>
                                    </AdminTableCell>
                                    <AdminTableCell className="tabular-nums">
                                        {new Date(
                                            user.created_at,
                                        ).toLocaleDateString()}
                                    </AdminTableCell>
                                </AdminTableRow>
                            ))}
                        </AdminTableBody>
                    </AdminTable>
                )}
            </AdminPageBody>
            <UserFormModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                mode="create"
                mutateUsers={mutateUsers}
            />
        </AdminPage>
    );
}
